import { ForwardedRef, HTMLAttributes, ReactNode, forwardRef } from 'react';
import styled from 'styled-components';

export type TableSpec<T extends string> = { [K in T]: ColSpec };

interface ColSpec {
  readonly header: ReactNode;
}

export function createTable<T extends string>(specObj: TableSpec<T>) {
  const spec = Object.entries(specObj as TableSpec<string>);

  const colGroup = (
    <colgroup>
      {spec.map(([id]) => (
        <col key={id} />
      ))}
    </colgroup>
  );

  const tableHead = (
    <thead css="position: relative; z-index: 1;">
      <tr>
        {spec.map(([id, colSpec]) => (
          <th key={id} scope="col">
            {colSpec.header}
          </th>
        ))}
      </tr>
    </thead>
  );

  function Table({ children }: { children: ReactNode }) {
    return (
      <StyledTable>
        {colGroup}
        {tableHead}
        <tbody>{children}</tbody>
      </StyledTable>
    );
  }

  const Row = forwardRef(function Row(
    props: { [K in T]: ReactNode } & HTMLAttributes<HTMLTableRowElement>,
    ref: ForwardedRef<HTMLTableRowElement>,
  ) {
    const cells = spec.map(([id], index) => {
      const content = props[id as T];

      if (index === 0) {
        return (
          <th key={id} scope="row">
            {content}
          </th>
        );
      }

      return <td key={id}>{content}</td>;
    });

    return (
      <tr onClick={props.onClick} ref={ref}>
        {cells}
      </tr>
    );
  });

  return [Table, Row] as const;
}

export const StyledTable = styled.table`
  table-layout: fixed;
  position: absolute;
  width: 100%;
  border-collapse: collapse;
  z-index: 0; // allows the tab underline to stay on top

  th,
  td {
    font-size: 12px;
    font-weight: 500;
    text-align: left;
    white-space: nowrap;
  }

  tr {
    height: 30px;
  }

  thead {
    color: rgba(255 255 255 / 0.5);
  }

  thead > tr > th {
    font-size: 11px;
    position: sticky;
    top: 0;
    white-space: normal;
  }

  tbody {
    color: inherit;
  }
`;
